/**
 * Workspace navigation presets (R17 — Founder mode). Pure data + pure helpers.
 *
 * `team` / `agency` / `enterprise` render the full hub tree unchanged. `founder`
 * renders ONE focused hub (Today & strategy · Content & publishing · Analytics ·
 * Copilot · Settings) and moves every other route into a single "More" hub whose
 * sub-sections mirror the original hubs — nothing disappears, every deep link
 * still resolves (routing is untouched; only the sidebar grouping changes).
 *
 * Routes listed here that are not (yet) in the nav catalog — `/today`,
 * `/strategy-mktg/brief`, `/catches`, `/launch-kit`, `/analytics/install`,
 * `/analytics/site-audit` … shipped by later increments — are skipped until
 * their catalog entry exists, then appear automatically in the founder hub.
 */
import type { WorkspacePreset } from '@/lib/api/orgSettings';

import { getNavItem, type NavHub, type NavItem, type NavSubGroup } from './navCatalog';

export interface FounderSection {
  id: 'home' | 'content' | 'analytics' | 'copilot' | 'settings';
  routes: string[];
}

/** The founder core, in display order (gap analysis §D.1 R17). */
export const FOUNDER_SECTIONS: FounderSection[] = [
  {
    id: 'home',
    routes: ['/today', '/autopilot', '/', '/strategy-mktg/brief', '/strategy-mktg', '/recommandations', '/catches'],
  },
  {
    id: 'content',
    routes: [
      '/content',
      '/content/new',
      '/content/studio',
      '/calendar',
      '/publishing',
      '/publishing/queue',
      '/platforms',
      '/media-library',
      '/launch-kit',
    ],
  },
  {
    id: 'analytics',
    routes: [
      '/analytics',
      '/analytics/install',
      '/analytics/funnels',
      '/analytics/attribution',
      '/analytics/geo',
      '/analytics/site-audit',
    ],
  },
  { id: 'copilot', routes: ['/ai-assistant'] },
  {
    id: 'settings',
    routes: [
      '/settings/organization',
      '/settings/subscription',
      '/settings/connections',
      '/settings/case-studies',
      '/settings',
    ],
  },
];

export const FOUNDER_HUB_ID = 'hub.founder';
export const MORE_HUB_ID = 'hub.more';

export interface PresetLabels {
  founderHub: string;
  more: string;
  groups: Record<FounderSection['id'], string>;
}

function hubItems(hub: NavHub): NavItem[] {
  return hub.subGroups ? hub.subGroups.flatMap((s) => s.items) : (hub.items ?? []);
}

/** Every founder-core route that currently resolves to a nav item. */
export function founderCoreRoutes(): Set<string> {
  const out = new Set<string>();
  for (const s of FOUNDER_SECTIONS) {
    for (const r of s.routes) if (getNavItem(r)) out.add(r);
  }
  return out;
}

/**
 * Re-shape the hub tree for a preset. Non-founder presets return `hubs`
 * untouched (same array reference) so the default rendering path is unchanged.
 */
export function applyWorkspacePreset(hubs: NavHub[], preset: WorkspacePreset, labels: PresetLabels): NavHub[] {
  if (preset !== 'founder') return hubs;

  const used = new Set<string>();
  const founderSubGroups: NavSubGroup[] = [];
  for (const section of FOUNDER_SECTIONS) {
    const items: NavItem[] = [];
    for (const route of section.routes) {
      const item = getNavItem(route);
      if (!item || used.has(item.to)) continue;
      used.add(item.to);
      items.push(item);
    }
    if (items.length > 0) {
      founderSubGroups.push({ id: `founder.${section.id}`, label: labels.groups[section.id], items });
    }
  }

  // "More": every other hub item, grouped under its original hub label.
  const moreSubGroups: NavSubGroup[] = [];
  for (const hub of hubs) {
    const rest = hubItems(hub).filter((i) => !used.has(i.to));
    if (rest.length > 0) {
      moreSubGroups.push({ id: `more.${hub.id}`, label: hub.label, items: rest });
    }
  }

  const out: NavHub[] = [
    { id: FOUNDER_HUB_ID, label: labels.founderHub, icon: 'dashboard', subGroups: founderSubGroups },
  ];
  if (moreSubGroups.length > 0) {
    out.push({ id: MORE_HUB_ID, label: labels.more, icon: 'projects', subGroups: moreSubGroups });
  }
  return out;
}
